import store from "../app/store";

/**
 * Fetches the list of pending tasks for the user, including crawl tasks and process tasks per bot.
 *
 * - Makes a `GET` request to `/user/getUserTasks`.
 * - Uses the bearer token from Redux store for authentication.
 * - Returns structured data with crawl tasks and a process mapping keyed by `botId`.
 *
 * @async
 * @function GetListOfPendingTask
 * @returns {Promise<{
 *   crawl: any[],
 *   process: Record<string, any[]>, // botId -> list of pending tasks
 *   error: boolean,
 *   msg?: string
 * }>} A promise resolving to an object:
 * - `crawl`: Array of crawl-related pending tasks.
 * - `process`: Object where keys are `botId`s and values are arrays of pending tasks.
 * - `error`: Indicates whether an error occurred (`true`/`false`).
 * - `msg` (optional): Error message for debugging or display.
 *
 * @example
 * const result = await GetListOfPendingTask();
 * if (!result.error) {
 *   console.log("Crawl Tasks:", result.crawl);
 *   console.log("Process Tasks by Bot:", result.process);
 * } else {
 *   console.error("Error:", result.msg);
 * }
 */

async function GetListOfPendingTask() {
  const domain = process.env.REACT_APP_SERVER;
  const url = `${domain}/user/getUserTasks`;

  const token = store.getState().auth.token;

  const options = {
    method: "GET",
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };

  try {
    const response = await fetch(url, options);

    if (!response.ok) {
      return { crawl: [], process: {}, error: true };
    }
    const data = await response.json();
    return {
      crawl: data?.crawl || [],
      process: data?.process || {},
      error: false,
    };
  } catch (error) {
    console.error("Error fetching tasks:", error);
    return { crawl: [], process: {}, error: true };
  }
}

export default GetListOfPendingTask;
